import React, { useEffect, useMemo, useState } from 'react';
import {
  Fab,
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Divider,
  Collapse,
  Button,
  Tooltip
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SchoolIcon from '@mui/icons-material/School';
import ArticleIcon from '@mui/icons-material/Article';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UploadIcon from '@mui/icons-material/Upload';
import FolderIcon from '@mui/icons-material/Folder';
import ExtensionIcon from '@mui/icons-material/Extension';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import {
  OnboardingContentSettings,
  OnboardingHelpArticle,
  SupportContentAudience,
} from '../../services/onboardingService';

interface HelpCenterProps {
  onRestartTour: () => void;
  content: OnboardingContentSettings['helpCenter'];
  viewerAudience: SupportContentAudience;
}

const canViewAudience = (
  itemAudience: SupportContentAudience,
  viewerAudience: SupportContentAudience
) => {
  if (itemAudience === 'all') return true;
  if (itemAudience === 'editor') {
    return viewerAudience === 'editor' || viewerAudience === 'admin';
  }
  return viewerAudience === 'admin';
};

const getArticleIcon = (article: OnboardingHelpArticle) => {
  const category = article.category.trim().toLowerCase();

  if (article.id === 'badges') {
    return <CheckCircleIcon />;
  }
  if (category === 'grundläggande' || category === 'grundlaggande') {
    if (article.id.includes('search')) return <SearchIcon />;
    if (article.id.includes('download')) return <DownloadIcon />;
    return <ArticleIcon />;
  }
  if (category === 'samlingar') {
    return <FolderIcon />;
  }
  if (category === 'uppladdning') {
    return <UploadIcon />;
  }
  if (category === 'grafisk manual') {
    return <SchoolIcon />;
  }
  if (category === 'plugins' || category === 'admin & redaktör' || category === 'admin & redaktor') {
    return <ExtensionIcon />;
  }
  if (category === 'prompts') {
    return <PlayCircleOutlineIcon />;
  }
  return <ArticleIcon />;
};

const HelpCenter: React.FC<HelpCenterProps> = ({
  onRestartTour,
  content,
  viewerAudience,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const visibleArticles = useMemo(
    () =>
      content.articles.filter((article) => canViewAudience(article.audience, viewerAudience)),
    [content.articles, viewerAudience]
  );

  const categories = useMemo(
    () => Array.from(new Set(visibleArticles.map((article) => article.category))),
    [visibleArticles]
  );

  useEffect(() => {
    if (!expandedCategory && categories.length > 0) {
      setExpandedCategory(categories[0]);
      return;
    }

    if (expandedCategory && !categories.includes(expandedCategory)) {
      setExpandedCategory(categories[0] ?? null);
    }
  }, [categories, expandedCategory]);

  const filteredArticles = useMemo(
    () =>
      visibleArticles.filter((article) => {
        const query = searchTerm.toLowerCase();
        return (
          article.title.toLowerCase().includes(query) ||
          article.content.toLowerCase().includes(query) ||
          article.category.toLowerCase().includes(query)
        );
      }),
    [searchTerm, visibleArticles]
  );

  const handleToggleDrawer = () => {
    setOpen((current) => !current);
  };

  const handleArticleClick = (articleId: string) => {
    setExpandedArticle((current) => (current === articleId ? null : articleId));
  };

  const handleCategoryClick = (category: string) => {
    setExpandedCategory((current) => (current === category ? null : category));
  };

  return (
    <>
      <Tooltip title={content.drawerTitle || 'Hjälpcenter'} placement="left">
        <Fab
          color="primary"
          onClick={handleToggleDrawer}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1200,
            bgcolor: '#1B3967',
            '&:hover': {
              bgcolor: '#0D1F3C'
            }
          }}
        >
          <HelpOutlineIcon />
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleToggleDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 380 },
            maxWidth: '100%'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              bgcolor: '#1B3967',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HelpOutlineIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {content.drawerTitle}
              </Typography>
            </Box>
            <IconButton onClick={handleToggleDrawer} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={content.searchPlaceholder}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Box>

          <Divider />

          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              {content.quickActionsTitle}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  onRestartTour();
                  setOpen(false);
                }}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {content.restartTourLabel}
              </Button>
            </Box>
          </Box>

          <Divider />

          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <Typography variant="subtitle2" sx={{ px: 2, pt: 2, color: 'text.secondary' }}>
              {content.articlesHeading}
            </Typography>

            {searchTerm ? (
              <List dense>
                {filteredArticles.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary={content.emptySearchTitle}
                      secondary={content.emptySearchDescription}
                    />
                  </ListItem>
                ) : (
                  filteredArticles.map((article) => (
                    <React.Fragment key={article.id}>
                      <ListItemButton onClick={() => handleArticleClick(article.id)}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getArticleIcon(article)}
                        </ListItemIcon>
                        <ListItemText
                          primary={article.title}
                          secondary={article.category}
                        />
                        {expandedArticle === article.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </ListItemButton>
                      <Collapse in={expandedArticle === article.id}>
                        <Box sx={{ px: 3, py: 2, bgcolor: '#f5f5f5' }}>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}
                          >
                            {article.content}
                          </Typography>
                        </Box>
                      </Collapse>
                    </React.Fragment>
                  ))
                )}
              </List>
            ) : categories.length === 0 ? (
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={content.emptySearchTitle}
                    secondary={content.emptySearchDescription}
                  />
                </ListItem>
              </List>
            ) : (
              categories.map((category) => (
                <Box key={category}>
                  <ListItemButton onClick={() => handleCategoryClick(category)}>
                    <ListItemText
                      primary={category}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                    {expandedCategory === category ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                  <Collapse in={expandedCategory === category}>
                    <List dense disablePadding>
                      {visibleArticles
                        .filter((article) => article.category === category)
                        .map((article) => (
                          <React.Fragment key={article.id}>
                            <ListItemButton
                              onClick={() => handleArticleClick(article.id)}
                              sx={{ pl: 4 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                {getArticleIcon(article)}
                              </ListItemIcon>
                              <ListItemText primary={article.title} />
                              {expandedArticle === article.id ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </ListItemButton>
                            <Collapse in={expandedArticle === article.id}>
                              <Box sx={{ px: 4, py: 2, bgcolor: '#f5f5f5', ml: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}
                                >
                                  {article.content}
                                </Typography>
                              </Box>
                            </Collapse>
                          </React.Fragment>
                        ))}
                    </List>
                  </Collapse>
                </Box>
              ))
            )}
          </Box>

          {!!content.footerText && (
            <Box sx={{ p: 2, borderTop: '1px solid #eee', bgcolor: '#fafafa' }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center', whiteSpace: 'pre-line' }}
              >
                {content.footerText}
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default HelpCenter;

